import type { StatusTrackerStepData } from '@/components/common';
import { InvoiceStatus } from '@/api/generated/models/invoiceStatus';
import i18n from '@/i18n';

export const statusConfig: Record<InvoiceStatus, { labelKey: string; bg: string; fg: string }> = {
  [InvoiceStatus.DRAFT]: {
    labelKey: 'invoices.status.draft',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [InvoiceStatus.APPROVED]: {
    labelKey: 'invoices.status.approved',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [InvoiceStatus.SENT]: {
    labelKey: 'invoices.status.sent',
    bg: 'var(--color-status-warning-soft)',
    fg: 'var(--color-status-warning)'
  },
  [InvoiceStatus.ACCEPTED]: {
    labelKey: 'invoices.status.accepted',
    bg: 'var(--color-status-success-soft)',
    fg: 'var(--color-status-success)'
  },
  [InvoiceStatus.REJECTED]: {
    labelKey: 'invoices.status.rejected',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  },
  [InvoiceStatus.PARTIALLY_PAID]: {
    labelKey: 'invoices.status.partiallyPaid',
    bg: 'var(--color-status-warning-soft)',
    fg: 'var(--color-status-warning)'
  },
  [InvoiceStatus.PAID]: {
    labelKey: 'invoices.status.paid',
    bg: 'var(--color-status-success-soft)',
    fg: 'var(--color-status-success)'
  },
  [InvoiceStatus.CLOSED]: {
    labelKey: 'invoices.status.closed',
    bg: 'var(--color-status-success-soft)',
    fg: 'var(--color-status-success)'
  },
  [InvoiceStatus.CANCELLED]: {
    labelKey: 'invoices.status.cancelled',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  },
  [InvoiceStatus.OVERDUE]: {
    labelKey: 'invoices.status.overdue',
    bg: 'var(--color-status-danger-soft)',
    fg: 'var(--color-status-danger)'
  },
  [InvoiceStatus.CREDITED]: {
    labelKey: 'invoices.status.credited',
    bg: 'var(--color-bg-surface-2)',
    fg: 'var(--color-text-secondary)'
  }
};

const FLOW_ORDER = [
  InvoiceStatus.DRAFT,
  InvoiceStatus.APPROVED,
  InvoiceStatus.SENT,
  InvoiceStatus.ACCEPTED,
  InvoiceStatus.PAID,
  InvoiceStatus.CLOSED
];

/**
 * Koraci za StatusTracker — isti princip kao offerDisplay.ts (buildOfferTrackerSteps). Klijentova
 * kopija nastaje izravno kao SENT (nikad ne prolazi DRAFT/APPROVED, to su isključivo partnerovi
 * interni koraci), pa klijent-view preskače ta dva koraka — isto pravilo kao Ponude ("View=Klijent
 * počinje od trenutka kad klijentova kopija nastaje"). OVERDUE = "paused" na koraku Poslana,
 * PARTIALLY_PAID prikazan kao trenutni korak Plaćena (djelomično je i dalje "u tijeku plaćanja", ne
 * zaseban korak). CANCELLED/CREDITED/REJECTED su iznimke koje mogu nastupiti iz više točaka tijeka
 * — konzervativno se prikazuje samo prvi korak (po view-u) kao "done" (isto obrazloženje kao
 * Contract CANCELLED u contractDisplay.ts). accept()/reject() mijenjaju OBJE kopije (isti obrazac
 * kao Offer), pa partner isto može vidjeti ACCEPTED/REJECTED na svojoj kopiji.
 */
export const buildInvoiceTrackerSteps = (
  status: InvoiceStatus,
  isPartnerView: boolean
): StatusTrackerStepData[] => {
  const t = i18n.t.bind(i18n);
  const labelFor = (s: InvoiceStatus) => t(statusConfig[s].labelKey);
  const firstVisibleStep = isPartnerView ? InvoiceStatus.DRAFT : InvoiceStatus.SENT;

  if (
    status === InvoiceStatus.CANCELLED ||
    status === InvoiceStatus.CREDITED ||
    status === InvoiceStatus.REJECTED
  ) {
    return [
      { key: 'start', label: labelFor(firstVisibleStep), state: 'done' },
      { key: 'exception', label: labelFor(status), state: 'exception' }
    ];
  }

  const effectiveStatus =
    status === InvoiceStatus.OVERDUE || status === InvoiceStatus.PARTIALLY_PAID
      ? status === InvoiceStatus.OVERDUE
        ? InvoiceStatus.SENT
        : InvoiceStatus.PAID
      : status;
  const visibleFlow = isPartnerView
    ? FLOW_ORDER
    : FLOW_ORDER.filter((s) => s !== InvoiceStatus.DRAFT && s !== InvoiceStatus.APPROVED);
  const currentIndex = visibleFlow.indexOf(effectiveStatus);

  return visibleFlow.map((s, index) => {
    let state: StatusTrackerStepData['state'];
    if (index < currentIndex) state = 'done';
    else if (index > currentIndex) state = 'upcoming';
    else state = status === InvoiceStatus.OVERDUE ? 'paused' : 'current';
    return { key: s, label: labelFor(s), state };
  });
};

/**
 * payments.payment_method je slobodan tekst (nema backend enum), pa korisnik može upisati bilo
 * što preko FormCreatableSelect "+ Dodaj ..." opcije. Ova mapa pokriva samo poznate kanonske
 * vrijednosti (stare seed vrijednosti u snake_case + dosadašnji hrvatski/engleski/njemački
 * preset tekstovi iz RecordPaymentModal-a) radi prikaza na trenutnom jeziku sučelja — bilo koji
 * drugi, stvarno proizvoljan unos prikazuje se nepromijenjen (nema ga što prevoditi).
 */
const PAYMENT_METHOD_LABEL_KEYS: Record<string, string> = {
  bank_transfer: 'paymentMethod.bankTransfer',
  'bankovni transfer': 'paymentMethod.bankTransfer',
  'bank transfer': 'paymentMethod.bankTransfer',
  überweisung: 'paymentMethod.bankTransfer',
  banküberweisung: 'paymentMethod.bankTransfer',
  cash: 'paymentMethod.cash',
  gotovina: 'paymentMethod.cash',
  bar: 'paymentMethod.cash',
  card: 'paymentMethod.card',
  kartica: 'paymentMethod.card',
  karte: 'paymentMethod.card'
};

export const formatPaymentMethod = (value?: string | null): string | null => {
  if (!value) return null;
  const key = PAYMENT_METHOD_LABEL_KEYS[value.trim().toLowerCase()];
  return key ? i18n.t(key) : value;
};

export const PAYMENT_METHOD_PRESET_KEYS = [
  'paymentMethod.bankTransfer',
  'paymentMethod.cash',
  'paymentMethod.card'
];
